import { Component, OnInit, OnDestroy } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import * as icons from '@fortawesome/free-solid-svg-icons';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NotificationService } from '../../shared/services/notification.service';

/**
 * Board interface for super admin board management.
 * Represents the minimal board data needed for the super admin UI.
 */
interface Board {
  id: string;
  config?: {
    instoreChannelPurchased?: boolean;
    phoneChannelPurchased?: boolean;
    internetChannelPurchased?: boolean;
  };
}

/**
 * Super Admin Board Management Component
 * 
 * Provides a UI for super admins to view all boards in the system
 * and toggle purchased feature flags (phone/internet channels).
 * 
 * Access is restricted via SuperAdminGuard on the route.
 * 
 * @see docs/stories/4.3.super-admin-board-management-ui.md
 */
@Component({
  selector: 'app-super-admin',
  templateUrl: './super-admin.component.html',
  styleUrls: ['./super-admin.component.css']
})
export class SuperAdminComponent implements OnInit, OnDestroy {

  icons = icons;
  
  /** All boards loaded from Firestore */
  boards: Board[] = [];
  
  /** Filtered boards based on search term */
  filteredBoards: Board[] = [];
  
  /** Current search filter text */
  searchTerm: string = '';
  
  /** Board ID currently being saved (for loading indicator) */
  savingBoardId: string | null = null;
  
  /** Loading state for initial data fetch */
  loading: boolean = true;

  private destroy$ = new Subject<void>();

  constructor(
    private db: AngularFirestore,
    private notificationService: NotificationService
  ) { }

  ngOnInit(): void {
    // Fetch all boards with real-time updates
    // Super admin has read access to all boards via Firestore rules (Story 4.1)
    this.db.collection<Board>('boards')
      .valueChanges({ idField: 'id' })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (boards) => {
          this.boards = boards;
          this.applyFilter();
          this.loading = false;
        },
        error: (error) => {
          console.error('Error fetching boards:', error);
          this.notificationService.showError('Failed to load boards. Please refresh the page.');
          this.loading = false;
        }
      });
  }

  /**
   * Applies the search filter to the boards list.
   * Filters by board ID (case-insensitive).
   */
  applyFilter(): void {
    if (!this.searchTerm.trim()) {
      this.filteredBoards = this.boards;
    } else {
      const term = this.searchTerm.toLowerCase();
      this.filteredBoards = this.boards.filter(b => 
        b.id.toLowerCase().includes(term)
      );
    }
  }

  /**
   * Toggles a purchased flag for a board and saves to Firestore.
   * 
   * @param boardId The ID of the board to update
   * @param flag The purchased flag to toggle
   * @param currentValue The current value of the flag
   */
  async togglePurchasedFlag(
    boardId: string, 
    flag: 'instoreChannelPurchased' | 'phoneChannelPurchased' | 'internetChannelPurchased', 
    currentValue: boolean
  ): Promise<void> {
    // Prevent rapid clicking
    if (this.savingBoardId !== null) {
      return;
    }

    this.savingBoardId = boardId;
    
    try {
      // Get friendly flag name for notifications
      const flagNames: Record<string, string> = {
        instoreChannelPurchased: 'In-Store Channel',
        phoneChannelPurchased: 'Phone Channel',
        internetChannelPurchased: 'Internet Channel'
      };
      const flagName = flagNames[flag];
      const newValue = !currentValue;

      // Update Firestore with merge to preserve other config values
      await this.db.collection('boards').doc(boardId).set(
        { config: { [flag]: newValue } }, 
        { merge: true }
      );
      
      this.notificationService.showSuccess(
        `${flagName} ${newValue ? 'enabled' : 'disabled'} for board`
      );
    } catch (error) {
      console.error('Error updating purchased flag:', error);
      this.notificationService.showError('Failed to update. Please try again.');
    } finally {
      this.savingBoardId = null;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
